import { LineSeparator } from '@/components/LineSeparator';
import { Loading } from '@/components/Loading';
import { OnboardingTour } from '@/components/Onboarding/OnboardingTour';
import { PremiumFeature } from '@/components/Stripe/PremiumFeature';
import { Text } from '@/components/Text';
import { getOnboardingSteps } from '@/constants/onboardingSteps';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSubscription } from '@/hooks/useSubscription';
import { UpdateCardAccessories } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardAccessories';
import { UpdateCardFollowUpEmail } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardFollowUpEmail';
import { UpdateCardInformations } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardInformations';
import { UpdateCardLeadCapture } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardLeadCapture';
import { UpdateCardLinksIndex } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardLinks/UpdateCardLinksIndex';
import { UpdateCardLinksCallToAction } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardLinksCallToAction';
import { UpdateCardMenu } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardMenu';
import { UpdateCardPhone } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardPhone';
import { UpdateCardQrCode } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardQrCode';
import { UpdateCardSettings } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardSettings';
import { UpdateCardStatistics } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardStatistics';
// UpdateCardViewSwitcher import removed as it's no longer used
import { useAuthStore } from '@/stores/useAuthStore';
import { useCardStore } from '@/stores/useCardStore';
import { useOnboardingStore } from '@/stores/useOnboardingStore';
import i18n from '@/translations/config';
import { Card, MenuItem } from '@/types/card';
import { cn } from '@/utils/cn';
import { handleError } from '@/utils/handleError';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEye } from 'react-icons/fa';
import {
  IoBarChartOutline,
  IoClose,
  IoFilter,
  IoGridOutline,
  IoIdCardOutline,
  IoLinkOutline,
  IoMailOutline,
  IoPersonOutline,
  IoQrCodeOutline,
  IoSettingsOutline,
} from 'react-icons/io5';
import { LuExternalLink } from 'react-icons/lu';
import {
  RiCheckLine,
  RiEyeLine,
  RiEyeOffLine,
  RiPencilLine,
} from 'react-icons/ri';
import { useNavigate, useParams } from 'react-router-dom';

export const UpdateCard = () => {
  const { slug } = useParams<{ slug: string }>();
  const {
    showCard,
    selectedMenuItem,
    setSelectedMenuItem,
    card,
    updateCard,
    isFetching,
  } = useCardStore();
  const { user } = useAuthStore();
  const { hasActiveSubscription } = useSubscription(user ?? null);
  const { t } = useTranslation(['updateCard', 'common']);
  const [activeView, setActiveView] = useState<'edit' | 'preview'>('edit');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [cardName, setCardName] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [showPreview, setShowPreview] = useState(true);
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const navigate = useNavigate();
  const { isNavigating, setIsNavigating } = useOnboardingStore();

  // Détecter si UpgradeCallToAction est affiché
  const showUpgradeCallToAction =
    user && user.seats.length === 0 && !hasActiveSubscription;

  const menuItems: MenuItem[] = useMemo(
    () => [
      {
        id: 1,
        title: (
          <div className="flex items-center gap-2">
            <IoPersonOutline className="text-lg" />
            <span>{t('updateCard:menu.profile')}</span>
          </div>
        ),
        component: UpdateCardInformations,
        showPreview: true,
        premium: false,
      },
      {
        id: 2,
        title: (
          <div className="flex items-center gap-2">
            <IoLinkOutline className="text-lg" />
            <span>{t('updateCard:menu.links')}</span>
          </div>
        ),
        component: UpdateCardLinksIndex,
        showPreview: true,
        premium: false,
      },
      {
        id: 3,
        title: (
          <div className="flex items-center gap-2">
            <IoFilter className="text-lg" />
            <span>{t('updateCard:menu.lead-capture')}</span>
          </div>
        ),
        component: UpdateCardLeadCapture,
        showPreview: true,
        premium: false,
        feature: 'plus',
      },
      {
        id: 4,
        title: (
          <div className="flex items-center gap-2">
            <IoMailOutline className="text-lg" />
            <span>{t('updateCard:menu.follow-up-email')}</span>
          </div>
        ),
        component: UpdateCardFollowUpEmail,
        showPreview: false,
        premium: false,
        feature: 'plus',
      },
      {
        id: 5,
        title: (
          <div className="flex items-center gap-2">
            <IoBarChartOutline className="text-lg" />
            <span>{t('updateCard:menu.statistics')}</span>
          </div>
        ),
        component: UpdateCardStatistics,
        showPreview: false,
        premium: true,
        feature: 'plus',
      },
      {
        id: 6,
        title: (
          <div className="flex items-center gap-2">
            <IoQrCodeOutline className="text-lg" />
            <span>{t('updateCard:menu.qr')}</span>
          </div>
        ),
        component: UpdateCardQrCode,
        showPreview: false,
        premium: false,
      },
      {
        id: 7,
        title: (
          <div className="flex items-center gap-2">
            <IoIdCardOutline className="text-lg" />
            <span>{t('updateCard:menu.accessories')}</span>
          </div>
        ),
        component: UpdateCardAccessories,
        showPreview: false,
        premium: false,
      },

      {
        id: 8,
        title: (
          <div className="flex items-center gap-2">
            <IoSettingsOutline className="text-lg" />
            <span>{t('updateCard:menu.settings')}</span>
          </div>
        ),
        component: UpdateCardSettings,
        showPreview: false,
        premium: false,
      },
    ],
    [t],
  );

  const handleMenuItemClick = (itemId: number) => {
    setSelectedMenuItem(itemId);
  };

  useEffect(() => {
    if (slug) {
      showCard(slug);
    }
  }, [slug, showCard]);

  const handleUpdateCard = (card: Card) => {
    try {
      updateCard(card.slug, card);
    } catch (error) {
      handleError(error, undefined, navigate);
    }
  };

  useEffect(() => {
    if (isNavigating) {
      setTimeout(() => {
        setIsNavigating(false);
      }, 100);
    }
  }, [isNavigating, setIsNavigating]);

  useEffect(() => {
    // Open the menu if onboarding advances to a step that requires it (on mobile)
    const { run, stepIndex } = useOnboardingStore.getState();
    if (run && isMobile) {
      const onboardingSteps = getOnboardingSteps(t, isMobile);
      const currentStep = onboardingSteps[stepIndex];
      if (currentStep && typeof currentStep.requiresMenuItemId === 'number') {
        setIsMenuOpen(true);
      }
    }
  }, [selectedMenuItem, isMobile, t]);

  if (!card || isFetching) return <Loading />;

  return (
    <div className="flex h-full flex-col overflow-x-hidden">
      <OnboardingTour
        cardSlugForTour={slug}
        selectedMenuItem={selectedMenuItem}
        setSelectedMenuItem={setSelectedMenuItem}
      />

      {/* Main Content Area */}
      <div className="min-h-0 flex-1 overflow-x-hidden">
        {isDesktop ? (
          <div className="flex h-full flex-col">
            {/* Call to action for adding links if none exist */}
            {card?.links &&
              card.links.length === 0 &&
              selectedMenuItem !== 2 && (
                <UpdateCardLinksCallToAction
                  onAddLinks={() => setSelectedMenuItem(2)}
                  className="mt-2 px-4 py-2"
                />
              )}

            <div
              className={cn(
                'flex min-h-0 flex-1 gap-3 overflow-x-hidden px-4 xl:gap-4',
                selectedMenuItem !== 2 && card.links?.length === 0
                  ? showUpgradeCallToAction
                    ? 'max-h-[calc(100vh-163px)]'
                    : 'max-h-[calc(100vh-115px)]'
                  : showUpgradeCallToAction
                    ? 'max-h-[calc(100vh-63px)] py-[15px]'
                    : 'py-[15px]', // Sans UpgradeCallToAction
              )}
            >
              {/* Left Content Area with Integrated Menu */}
              <div
                id="update-card-left-panel"
                className={cn(
                  'flex min-h-0 flex-col overflow-hidden rounded-xl bg-white shadow-md',
                  selectedMenuItem !== null &&
                    (!menuItems.find(item => item.id === selectedMenuItem)
                      ?.showPreview ||
                      !showPreview)
                    ? 'flex-1'
                    : 'w-3/5 xl:w-2/3',
                )}
              >
                {/* Card Header */}
                <div className="shrink-0 overflow-x-hidden border-b border-gray-100 bg-gradient-to-br from-gray-50 to-white p-4">
                  <div className="flex min-w-0 items-center justify-between gap-4">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      {isEditingName ? (
                        <div className="flex w-full min-w-0 items-center gap-2">
                          <input
                            ref={nameInputRef}
                            type="text"
                            value={cardName}
                            onChange={e => setCardName(e.target.value)}
                            className="min-w-0 flex-1 rounded-lg border border-gray-200 px-3 py-2 text-base font-medium text-gray-900 focus:border-linko-light focus:outline-none focus:ring-1 focus:ring-linko-light"
                            autoFocus
                            onKeyDown={async e => {
                              if (e.key === 'Enter') {
                                if (cardName.trim() !== '') {
                                  handleUpdateCard({
                                    ...card,
                                    name: cardName.trim(),
                                  });
                                  setIsEditingName(false);
                                }
                              } else if (e.key === 'Escape') {
                                setCardName(card.name);
                                setIsEditingName(false);
                              }
                            }}
                          />
                          <button
                            onClick={async () => {
                              if (cardName.trim() !== '') {
                                handleUpdateCard({
                                  ...card,
                                  name: cardName.trim(),
                                });
                                setIsEditingName(false);
                              }
                            }}
                            className="flex shrink-0 items-center justify-center rounded-lg bg-linko-light p-2 text-white active:scale-95"
                          >
                            <RiCheckLine className="size-5" />
                          </button>
                        </div>
                      ) : (
                        <div
                          className="group flex min-w-0 cursor-pointer items-center gap-2"
                          onClick={() => {
                            setIsEditingName(true);
                            setCardName(card.name);
                            setTimeout(() => nameInputRef.current?.focus(), 0);
                          }}
                        >
                          <Text className="min-w-0 truncate font-medium text-gray-900">
                            {card.name}
                          </Text>
                          <RiPencilLine className="size-4 shrink-0 text-gray-400 opacity-50 group-hover:opacity-100" />
                        </div>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        onClick={() => setShowPreview(!showPreview)}
                        className={cn(
                          'flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-200 active:scale-95',
                          selectedMenuItem === null ||
                            !menuItems.find(
                              item => item.id === selectedMenuItem,
                            )?.showPreview
                            ? 'hidden'
                            : '',
                        )}
                        title={
                          showPreview
                            ? t('updateCard:preview.hide-preview')
                            : t('updateCard:preview.show-preview')
                        }
                      >
                        {showPreview ? (
                          <>
                            <RiEyeOffLine className="size-4" />
                            <span className="hidden md:inline">
                              {t('updateCard:preview.hide-preview')}
                            </span>
                          </>
                        ) : (
                          <>
                            <RiEyeLine className="size-4" />
                            <span className="hidden md:inline">
                              {t('updateCard:preview.show-preview')}
                            </span>
                          </>
                        )}
                      </button>
                      <a
                        id="view-card-button"
                        href={`/${i18n.language}/profile/${card.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1.5 text-sm font-medium text-linko-light hover:bg-blue-100 hover:text-linko-light active:scale-95"
                      >
                        <span className="hidden md:inline">
                          {t('updateCard:view-card')}
                        </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="size-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                          <polyline points="15 3 21 3 21 9"></polyline>
                          <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Menu and Content Area */}
                <div className="flex min-h-0 flex-1 overflow-x-hidden">
                  {/* Vertical Menu */}
                  <div className="flex w-16 shrink-0 flex-col items-center gap-6 border-r border-gray-100 bg-gray-50 py-6">
                    <UpdateCardMenu
                      id="update-card-menu"
                      menuItems={menuItems}
                    />
                  </div>

                  {/* Content Area - Le scroll est uniquement ici */}
                  <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 xl:p-6">
                    {(() => {
                      const selectedItem = menuItems.find(
                        item => item.id === selectedMenuItem,
                      );
                      const premium = selectedItem?.premium;
                      const Component = selectedItem?.component;

                      const contentIdMap: { [key: number]: string } = {
                        1: 'update-card-informations-content',
                        2: 'update-card-links',
                        3: 'update-card-lead-capture-content',
                        4: 'update-card-follow-up-email-content',
                        5: 'update-card-statistics-content',
                        6: 'update-card-qr-code-content',
                        7: 'update-card-accessories-content',
                        8: 'update-card-settings-content',
                      };

                      const contentId = selectedMenuItem
                        ? contentIdMap[selectedMenuItem]
                        : undefined;

                      if (!Component) return null;
                      return premium && selectedItem.feature ? (
                        <PremiumFeature
                          user={card?.user}
                          feature={selectedItem.feature}
                        >
                          <div id={contentId}>
                            <Component />
                          </div>
                        </PremiumFeature>
                      ) : (
                        <div id={contentId}>
                          <Component />
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Right Preview Column */}
              <div
                id="update-card-right-panel"
                className={cn(
                  'relative overflow-hidden rounded-xl',
                  selectedMenuItem !== null &&
                    (!menuItems.find(item => item.id === selectedMenuItem)
                      ?.showPreview ||
                      !showPreview)
                    ? 'hidden'
                    : 'w-2/5 shrink-0 xl:w-1/3',
                )}
              >
                <div className="absolute right-4 top-4 z-10 flex gap-2">
                  <a
                    id="view-card-button"
                    href={`/${i18n.language}/profile/${card.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1.5 text-sm font-medium text-linko-light hover:bg-blue-100 hover:text-linko-light active:scale-95"
                  >
                    <span>{t('updateCard:view-card')}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="size-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                  </a>
                </div>

                <div className="flex h-full flex-col items-center justify-center rounded-xl bg-gradient-to-b from-gray-50 to-white p-4 shadow-md xl:p-6">
                  <div className="mb-6 text-center">
                    <Text className="text-sm font-semibold text-gray-500">
                      {t('updateCard:preview.title')}
                    </Text>
                    <div className="mt-1 text-xs text-gray-400">
                      {t('updateCard:preview.description')}
                    </div>
                  </div>

                  <UpdateCardPhone />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-col overflow-x-hidden">
            {/* Floating Buttons - Moved outside of conditional rendering */}
            <div className="fixed bottom-28 right-4 z-40 flex flex-col gap-3">
              {/* View Toggle Button */}
              <button
                id="mobile-view-toggle"
                onClick={() =>
                  setActiveView(activeView === 'edit' ? 'preview' : 'edit')
                }
                className={cn(
                  'flex size-14 items-center justify-center rounded-full text-white shadow-lg transition-all duration-300 hover:shadow-xl active:scale-95',
                  activeView === 'edit'
                    ? 'bg-linko-dark hover:bg-linko-light'
                    : 'bg-linko-light hover:bg-linko-light',
                )}
              >
                {activeView === 'edit' ? (
                  <FaEye className="size-6" />
                ) : (
                  <RiPencilLine className="size-6" />
                )}
              </button>

              {/* Menu Button - Only show in edit mode */}
              {activeView === 'edit' && (
                <button
                  id="mobile-menu-trigger"
                  onClick={() => setIsMenuOpen(true)}
                  className="flex size-14 items-center justify-center rounded-full bg-linko-light text-white shadow-lg shadow-linko-light/25 transition-all duration-300 hover:bg-linko-dark hover:shadow-xl active:scale-95"
                >
                  <IoGridOutline className="size-6" />
                </button>
              )}
            </div>

            {activeView === 'edit' ? (
              <div className="flex h-full flex-col overflow-x-hidden">
                {/* Menu Modal */}
                {isMenuOpen && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm"
                      onClick={() => setIsMenuOpen(false)}
                    />

                    {/* Menu Content */}
                    <div className="fixed inset-x-2 top-1/2 z-[70] max-h-[70vh] -translate-y-1/2 overflow-hidden rounded-2xl bg-white/90 shadow-2xl backdrop-blur-lg">
                      {/* Header */}
                      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {t('updateCard:menu.title', 'Menu')}
                        </h3>
                        <button
                          onClick={() => setIsMenuOpen(false)}
                          className="flex size-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200"
                        >
                          <IoClose className="size-5" />
                        </button>
                      </div>

                      {/* Menu Items Grid */}
                      <div className="max-h-[50vh] overflow-y-auto p-4">
                        <div className="grid grid-cols-2 gap-3">
                          {menuItems.map(item => (
                            <button
                              key={item.id}
                              id={`mobile-menu-item-${item.id}`}
                              onClick={() => {
                                handleMenuItemClick(item.id);
                                setIsMenuOpen(false);
                              }}
                              className={cn(
                                'flex flex-col items-center gap-3 rounded-xl p-4 text-center transition-all duration-200',
                                selectedMenuItem === item.id
                                  ? 'bg-linko-light text-white shadow-lg shadow-linko-light/25'
                                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100',
                              )}
                            >
                              {React.isValidElement(item.title) &&
                                React.Children.map(
                                  item.title.props.children,
                                  (child, index) => {
                                    if (index === 0) {
                                      return (
                                        <div className="flex size-12 items-center justify-center rounded-full bg-white/20">
                                          {React.cloneElement(child, {
                                            className: cn(
                                              'text-2xl',
                                              selectedMenuItem === item.id
                                                ? 'text-white'
                                                : 'text-gray-600',
                                            ),
                                          })}
                                        </div>
                                      );
                                    }
                                    return (
                                      <span className="text-sm font-medium">
                                        {child.props.children}
                                      </span>
                                    );
                                  },
                                )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Mobile Content Area - Le scroll est uniquement ici */}
                <div
                  className={cn(
                    'flex-1 overflow-y-auto overflow-x-hidden',
                    selectedMenuItem !== null &&
                      menuItems.find(item => item.id === selectedMenuItem)
                        ?.showPreview,
                  )}
                >
                  {/* Call to action for mobile */}
                  {card?.links &&
                    card.links.length === 0 &&
                    selectedMenuItem !== 2 && (
                      <UpdateCardLinksCallToAction
                        onAddLinks={() => setSelectedMenuItem(2)}
                        className="mx-4 mt-2 py-2"
                      />
                    )}

                  <div className="flex min-w-0 items-center gap-2 p-4">
                    {isEditingName ? (
                      <div className="flex w-full min-w-0 items-center gap-2">
                        <input
                          ref={nameInputRef}
                          type="text"
                          value={cardName}
                          onChange={e => setCardName(e.target.value)}
                          className="min-w-0 flex-1 rounded-lg border border-gray-200 px-3 py-2 text-base font-medium text-gray-900 focus:border-linko-light focus:outline-none focus:ring-1 focus:ring-linko-light"
                          autoFocus
                          onKeyDown={async e => {
                            if (e.key === 'Enter') {
                              if (cardName.trim() !== '') {
                                handleUpdateCard({
                                  ...card,
                                  name: cardName.trim(),
                                });
                                setIsEditingName(false);
                              }
                            } else if (e.key === 'Escape') {
                              setCardName(card.name);
                              setIsEditingName(false);
                            }
                          }}
                        />
                        <button
                          onClick={async () => {
                            if (cardName.trim() !== '') {
                              handleUpdateCard({
                                ...card,
                                name: cardName.trim(),
                              });
                              setIsEditingName(false);
                            }
                          }}
                          className="flex shrink-0 items-center justify-center rounded-lg bg-linko-light p-2 text-white active:scale-95"
                        >
                          <RiCheckLine className="size-5" />
                        </button>
                      </div>
                    ) : (
                      <div
                        className="group flex w-full min-w-0 cursor-pointer items-center gap-2"
                        onClick={() => {
                          setIsEditingName(true);
                          setCardName(card.name);
                          setTimeout(() => nameInputRef.current?.focus(), 0);
                        }}
                      >
                        <Text className="min-w-0 truncate text-lg font-medium text-gray-900">
                          {card.name}
                        </Text>
                        <RiPencilLine className="size-5 shrink-0 text-gray-400 opacity-50 group-hover:opacity-100" />
                      </div>
                    )}
                  </div>
                  <div className="mx-4 flex items-center justify-end">
                    <a
                      id="view-card-button-mobile"
                      href={`/${i18n.language}/profile/${card.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1.5 text-sm font-medium text-linko-light hover:bg-blue-100 hover:text-linko-light active:scale-95"
                    >
                      <span>{t('updateCard:view-card')}</span>
                      <LuExternalLink className="size-4" />
                    </a>
                  </div>

                  <LineSeparator className="m-4" />

                  {/* Component Content */}
                  <div className="overflow-x-hidden px-4 pb-4">
                    {(() => {
                      const selectedItem = menuItems.find(
                        item => item.id === selectedMenuItem,
                      );
                      const premium = selectedItem?.premium;
                      const Component = selectedItem?.component;

                      const contentIdMap: { [key: number]: string } = {
                        1: 'update-card-informations-content',
                        2: 'update-card-links',
                        3: 'update-card-lead-capture-content',
                        4: 'update-card-follow-up-email-content',
                        5: 'update-card-statistics-content',
                        6: 'update-card-qr-code-content',
                        7: 'update-card-accessories-content',
                        8: 'update-card-settings-content',
                      };

                      const contentId = selectedMenuItem
                        ? contentIdMap[selectedMenuItem]
                        : undefined;

                      if (!Component) return null;
                      return premium && selectedItem.feature ? (
                        <PremiumFeature
                          user={card?.user}
                          feature={selectedItem.feature}
                        >
                          <div id={contentId}>
                            <Component />
                          </div>
                        </PremiumFeature>
                      ) : (
                        <div id={contentId}>
                          <Component />
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={cn(
                  'flex-1 overflow-y-auto overflow-x-hidden',
                  selectedMenuItem !== null &&
                    menuItems.find(item => item.id === selectedMenuItem)
                      ?.showPreview &&
                    'pt-16',
                )}
              >
                <div className="flex items-center justify-center p-4">
                  <UpdateCardPhone />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
